import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  LARGURAS,
  larguraNaGrade,
  moverWidget,
  novoWidget,
  rascunhoDo,
  rascunhoVazio,
  useSalvarPainel,
  validarRascunho,
} from "../../nucleo/paineis";
import type {
  CatalogoDosPaineis,
  FiltrosGuardados,
  PainelNoConsole,
  RascunhoDePainel,
} from "../../nucleo/paineis";
import { FiltrosDoWidget } from "./FiltrosDoWidget";

interface Props {
  catalogo: CatalogoDosPaineis;
  /** Ausente = painel novo. */
  painel?: PainelNoConsole;
  aoSalvar: () => void;
  aoCancelar: () => void;
}

/**
 * A edição do painel numa grade simples: cada widget é um bloco com a largura que tem (1 a 4
 * colunas) e a posição que ocupa na lista — a ordem da lista é a ordem do painel. O catálogo é
 * fixo: a pessoa escolhe entre os widgets que existem, não desenha gráfico.
 */
export function EditorDePainel({ catalogo, painel, aoSalvar, aoCancelar }: Props) {
  const [rascunho, setRascunho] = useState<RascunhoDePainel>(
    painel ? rascunhoDo(painel) : rascunhoVazio(),
  );
  const [escolhido, setEscolhido] = useState("");
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const salvar = useSalvarPainel();
  const porTipo = new Map(catalogo.widgets.map((definicao) => [definicao.tipo, definicao]));

  function mudarWidget(chave: string, mudanca: { colunas?: number; filtros?: FiltrosGuardados }) {
    setRascunho((atual) => ({
      ...atual,
      widgets: atual.widgets.map((w) => (w.chave === chave ? { ...w, ...mudanca } : w)),
    }));
  }

  function acrescentar() {
    if (!escolhido) return;
    setRascunho((atual) => ({ ...atual, widgets: [...atual.widgets, novoWidget(escolhido)] }));
    setEscolhido("");
  }

  function alternarPapel(chave: string, marcado: boolean) {
    setRascunho((atual) => ({
      ...atual,
      papeis: marcado ? [...atual.papeis, chave] : atual.papeis.filter((papel) => papel !== chave),
    }));
  }

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const problema = validarRascunho(rascunho);
    setErroLocal(problema);
    if (problema) return;
    salvar.mutate({ id: painel?.id, rascunho }, { onSuccess: aoSalvar });
  }

  return (
    <form onSubmit={submeter} data-testid="editor-de-painel" noValidate>
      <Cartao titulo={painel ? "Editar painel" : "Novo painel"} className="mb-4">
        <Campo
          rotulo="Nome do painel"
          value={rascunho.nome}
          onChange={(evento) => setRascunho({ ...rascunho, nome: evento.target.value })}
          maxLength={120}
        />
        <CampoLongo
          rotulo="Descrição (opcional)"
          value={rascunho.descricao}
          onChange={(evento) => setRascunho({ ...rascunho, descricao: evento.target.value })}
        />
      </Cartao>

      <Cartao titulo="Widgets" className="mb-4">
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <label className="campo mb-0" htmlFor="novo-widget">
            <span className="campo__rotulo">Acrescentar widget</span>
            <select
              id="novo-widget"
              className="campo__controle"
              value={escolhido}
              onChange={(evento) => setEscolhido(evento.target.value)}
            >
              <option value="">Escolha um widget</option>
              {catalogo.widgets.map((definicao) => (
                <option key={definicao.tipo} value={definicao.tipo}>
                  {definicao.titulo}
                </option>
              ))}
            </select>
          </label>
          <Botao tamanho="pequeno" disabled={!escolhido} onClick={acrescentar}>
            Acrescentar
          </Botao>
        </div>
        {escolhido ? (
          <p className="nota-rodape mb-3">{porTipo.get(escolhido)?.descricao}</p>
        ) : null}
        {rascunho.widgets.length === 0 ? (
          <Aviso tom="neutro">Nenhum widget ainda. Escolha um acima para começar.</Aviso>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="grade-de-edicao">
            {rascunho.widgets.map((widget, indice) => {
              const definicao = porTipo.get(widget.tipo);
              return (
                <section
                  key={widget.chave}
                  className={`cartao ${larguraNaGrade(widget.colunas)}`}
                  aria-label={`${definicao?.titulo ?? widget.tipo}, posição ${indice + 1}`}
                  data-testid={`bloco-${indice}`}
                >
                  <h4 className="cartao__titulo">
                    {indice + 1}. {definicao?.titulo ?? widget.tipo}
                  </h4>
                  <label className="campo" htmlFor={`largura-${widget.chave}`}>
                    <span className="campo__rotulo">Largura</span>
                    <select
                      id={`largura-${widget.chave}`}
                      className="campo__controle"
                      value={widget.colunas}
                      onChange={(evento) =>
                        mudarWidget(widget.chave, { colunas: Number(evento.target.value) })
                      }
                    >
                      {LARGURAS.map((colunas) => (
                        <option key={colunas} value={colunas}>
                          {colunas} {colunas === 1 ? "coluna" : "colunas"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <FiltrosDoWidget
                    aceitos={definicao?.filtros ?? []}
                    valor={widget.filtros}
                    aoMudar={(filtros) => mudarWidget(widget.chave, { filtros })}
                    periodoPadrao={definicao?.periodo_padrao ?? ""}
                  />
                  <div className="flex gap-2 mt-3">
                    <Botao
                      tamanho="pequeno"
                      variante="fantasma"
                      disabled={indice === 0}
                      aria-label={`Subir ${definicao?.titulo ?? widget.tipo}`}
                      onClick={() =>
                        setRascunho({ ...rascunho, widgets: moverWidget(rascunho.widgets, indice, -1) })
                      }
                    >
                      Subir
                    </Botao>
                    <Botao
                      tamanho="pequeno"
                      variante="fantasma"
                      disabled={indice === rascunho.widgets.length - 1}
                      aria-label={`Descer ${definicao?.titulo ?? widget.tipo}`}
                      onClick={() =>
                        setRascunho({ ...rascunho, widgets: moverWidget(rascunho.widgets, indice, 1) })
                      }
                    >
                      Descer
                    </Botao>
                    <Botao
                      tamanho="pequeno"
                      variante="fantasma"
                      aria-label={`Tirar ${definicao?.titulo ?? widget.tipo}`}
                      onClick={() =>
                        setRascunho({
                          ...rascunho,
                          widgets: rascunho.widgets.filter((w) => w.chave !== widget.chave),
                        })
                      }
                    >
                      Tirar
                    </Botao>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </Cartao>

      <Cartao titulo="Quem recebe este painel como tela inicial" className="mb-4">
        <p className="cartao__texto mb-2">
          O painel vira a primeira tela do console para os papéis marcados — cada pessoa vê só os
          widgets que o acesso dela permite. Quem não tem painel continua no console de sempre.
        </p>
        <ul className="list-none p-0 m-0">
          {catalogo.papeis.map((papel) => {
            const ocupado = papel.painel_atual !== null && papel.painel_atual !== painel?.nome;
            return (
              <li key={papel.chave}>
                <label className="opcao">
                  <input
                    type="checkbox"
                    checked={rascunho.papeis.includes(papel.chave)}
                    disabled={ocupado}
                    onChange={(evento) => alternarPapel(papel.chave, evento.target.checked)}
                  />
                  <span className="flex-1">
                    <span className="opcao__titulo">{papel.nome}</span>
                    {ocupado ? (
                      <span className="opcao__descricao"> — hoje usa o painel “{papel.painel_atual}”</span>
                    ) : null}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </Cartao>

      {erroLocal ? <Aviso tom="erro" className="mb-3">{erroLocal}</Aviso> : null}
      {salvar.isError ? (
        <Aviso tom="erro" className="mb-3">{mensagemDoConsole(salvar.error)}</Aviso>
      ) : null}
      <div className="flex gap-2">
        <Botao type="submit" disabled={salvar.isPending}>
          {salvar.isPending ? "Salvando…" : "Salvar painel"}
        </Botao>
        <Botao variante="fantasma" onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}
